import React, { useState } from "react";
import { cn } from "../lib";
import { Button } from "./button";

export interface AnnouncementBannerProps {
  /** Text or element to display in the banner */
  message?: React.ReactNode;
  /** Custom icon component or element */
  icon?: React.ReactNode;
  /** Action button text. Set to null or empty string to hide dismiss button */
  dismissText?: string | null;
  /** Callback when dismiss button is clicked */
  onDismiss?: () => void;
  /** Visual variant tone */
  variant?: "success" | "warning" | "error" | "info";
  /** Additional CSS class names */
  className?: string;
  /** Custom style overrides */
  style?: React.CSSProperties;
  /** Controlled visibility state */
  isVisible?: boolean;
}

const variantStyles = {
  success: {
    container: "bg-[#DCFDDB]",
    text: "text-[#067647]",
    iconBg: "bg-[#12B76A]",
    dismissText: "text-[#067647] hover:text-[#044E2F]",
  },
  warning: {
    container: "bg-[#FFEFC6]",
    text: "text-[#B64607]",
    iconBg: "bg-[#F59E0B]",
    dismissText: "text-[#B64607] hover:text-[#8D3302]",
  },
  error: {
    container: "bg-[#FFE1E1]",
    text: "text-[#C01515]",
    iconBg: "bg-[#F04438]",
    dismissText: "text-[#C01515] hover:text-[#900B0B]",
  },
  info: {
    container: "bg-[#EBE4FF]",
    text: "text-[#5E01D6]",
    iconBg: "bg-[#6F00FF]",
    dismissText: "text-[#5E01D6] hover:text-[#43009C]",
  },
};

export const AnnouncementBanner: React.FC<AnnouncementBannerProps> = ({
  message = "Customer has submitted the requested information",
  icon,
  dismissText = "Dismiss",
  onDismiss,
  variant = "success",
  className,
  style: customStyle,
  isVisible = true,
}) => {
  const [internalVisible, setInternalVisible] = useState(true);

  if (!isVisible || !internalVisible) {
    return null;
  }

  const handleDismiss = () => {
    setInternalVisible(false);
    onDismiss?.();
  };

  const style = variantStyles[variant] || variantStyles.success;

  return (
    <div
      role="alert"
      className={cn(
        "flex w-full items-center justify-between px-4 py-3 rounded-t-[16px] rounded-b-none transition-all duration-200",
        style.container,
        className,
      )}
      style={{
        borderBottom: "0.5px solid var(--_blue-400, #5B92FF)",
        ...customStyle,
      }}
    >
      <div className="flex items-center gap-2.5 min-w-0">
        {icon ?? (
          <span
            className={cn(
              "flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-white text-[12px] font-bold leading-none select-none",
              style.iconBg,
            )}
          >
            !
          </span>
        )}
        <span
          className={cn(
            "aku-body-small-medium text-sm font-medium leading-tight truncate md:whitespace-normal",
            style.text,
          )}
        >
          {message}
        </span>
      </div>
      {dismissText && (
        <Button
          type="button"
          variant="ghost"
          onClick={handleDismiss}
          className={cn(
            "ml-4 h-auto px-2.5 py-1 shrink-0 text-sm font-medium aku-body-small-medium transition-colors hover:bg-black/5 no-underline focus-visible:ring-0",
            style.dismissText,
          )}
        >
          {dismissText}
        </Button>
      )}
    </div>
  );
};
